import {
  buildDataTableCols,
  buildInputTableCols,
  type DataTableCol,
  type DataTableColumn,
  type InputTableCol
} from '@/components/custom/erp';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import type { DonHang } from '@/types/schemas';

type DonHangRow = BanHangRecord;

const STATUS_OPTIONS = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '1', label: 'Chờ duyệt' },
  { value: '4', label: 'Đang duyệt' },
  { value: '5', label: 'Đã duyệt' },
  { value: '6', label: 'Đang thực hiện' },
  { value: '7', label: 'Hoàn thành' },
  { value: '9', label: 'Đóng' }
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: '5', label: 'Đơn hàng' },
  { value: '4', label: 'Hợp đồng' }
];

export const BASE_COLS: DataTableCol<DonHangRow>[] = [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    valueGetter: row => row?.status,
    valueFormatter: value => STATUS_OPTIONS.find(option => option.value === String(value ?? ''))?.label ?? '',
    filter: { type: 'multiple', options: STATUS_OPTIONS }
  },
  {
    field: 'so_ct',
    headerName: 'Số c/từ',
    width: 150,
    valueGetter: row => row?.so_ct,
    filter: { type: 'text' }
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 120,
    valueGetter: row => row?.ngay_ct,
    valueFormatter: value => formatDate(value as BanHangRecord),
    filter: { type: 'date' }
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 120,
    valueGetter: row => row?.ma_kh_data?.customer_code || row?.ma_kh || '',
    filter: { type: 'text' }
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    valueGetter: row => row?.ma_kh_data?.customer_name || row?.ten_kh || '',
    filter: { type: 'text' }
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200,
    valueGetter: row => row?.dien_giai,
    filter: { type: 'text' }
  },
  {
    field: 't_khoi_luong',
    headerName: 'Tổng khối lượng',
    width: 150,
    columnType: 'number',
    valueGetter: row => row?.t_khoi_luong,
    valueFormatter: value => formatRoundedNumber(Number(value ?? 0), 6),
    filter: { type: 'text' }
  },
  {
    field: 't_tt_nt',
    headerName: 'Tổng tiền',
    width: 180,
    columnType: 'number',
    valueGetter: row => row?.t_tt_nt,
    valueFormatter: value => formatRoundedNumber(Number(value ?? 0)),
    filter: { type: 'text' }
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 120,
    valueGetter: row => row?.ma_nt_data?.ma_nt || row?.ma_nt || '',
    filter: { type: 'text' }
  },
  {
    field: 'ma_ngv',
    headerName: 'Loại chứng từ',
    width: 120,
    valueGetter: row => row?.ma_ngv,
    valueFormatter: value => DOCUMENT_TYPE_OPTIONS.find(option => option.value === String(value ?? ''))?.label ?? '',
    filter: { type: 'multiple', options: DOCUMENT_TYPE_OPTIONS }
  }
];

export const DATA_TABLE_COLUMNS = (
  handleViewClick: (row?: DonHang) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn[] => {
  const total = (
    <div className='flex items-center gap-2'>
      <span className='text-lg font-bold'>&#931;</span>
      <span className='font-semibold text-main'>
        {isTotalLoading ? 'Đang tính...' : typeof totalAmount === 'number' ? formatRoundedNumber(totalAmount) : '—'}
      </span>
    </div>
  );

  return buildDataTableCols(
    BASE_COLS.map<DataTableCol<DonHangRow>>(col => {
      if (col.field === 'so_ct') {
        return {
          ...col,
          renderCell: params => (
            <button
              onClick={event => {
                event.stopPropagation();
                handleViewClick(params.row);
              }}
              className='hover:text-blue-500 hover:underline'
            >
              {params.row?.so_ct}
            </button>
          )
        };
      }

      if (col.field === 't_tt_nt') {
        return { ...col, filter: { type: 'text' as const, after: total } };
      }

      return col;
    })
  );
};

export const INPUT_TABLE_BASE_COLS: InputTableCol<BanHangRecord>[] = [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: params => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 100,
    renderCell: params => params.row.dvt_data?.dvt
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 120,
    renderCell: params => params.row.ma_kho_data?.ma_kho || params.row.ma_vt_data?.ma_kho_data?.ma_kho
  },
  {
    field: 'ma_khon',
    headerName: 'Mã kho nhận',
    width: 120,
    renderCell: params => params.row.ma_khon_data?.ma_kho || params.row.ma_khon || ''
  },
  {
    field: 'sl_ton',
    headerName: 'Tồn',
    width: 100,
    align: 'right'
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    columnType: 'number',
    valueGetter: row => row?.so_luong,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'khoi_luong',
    headerName: 'Khối lượng',
    width: 120,
    columnType: 'number',
    valueGetter: row => row?.khoi_luong,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'ct_km',
    headerName: 'Loại hàng',
    width: 120,
    renderCell: params => {
      switch (params.row.ct_km) {
        case false:
        case '0':
          return 'Hàng bán';
        case true:
        case '1':
          return 'Hàng KM';
        default:
          return '';
      }
    }
  },
  {
    field: 'ma_loai_gb',
    headerName: 'Loại giá',
    width: 150
  },
  {
    field: 'gia_nt1',
    headerName: 'Giá chuẩn VND',
    width: 150,
    columnType: 'number',
    valueGetter: row => row?.gia_nt1,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá bán VND',
    width: 150,
    columnType: 'number',
    valueGetter: row => row?.gia_nt2,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'tien_nt2',
    headerName: 'Thành tiền VND',
    width: 150,
    columnType: 'number',
    valueGetter: row => row?.tien_nt2,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'ngay_giao',
    headerName: 'Ngày giao',
    width: 150,
    valueGetter: row => row?.ngay_giao,
    valueFormatter: value => formatDate(value as BanHangRecord)
  },
  {
    field: 'tl_ck',
    headerName: 'Tl ck(%)',
    width: 150,
    columnType: 'number'
  },
  {
    field: 'ck_nt',
    headerName: 'Ch.khấu VND',
    width: 150,
    columnType: 'number',
    valueGetter: row => row?.ck_nt,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 100,
    renderCell: params => params.row.ma_thue_data?.ten_thue || params.row.ma_thue
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế VND',
    width: 100,
    columnType: 'number',
    valueGetter: row => row?.thue_nt,
    valueFormatter: value => formatMoney(Number(value ?? 0))
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: params => params.row.ma_vv_data?.ma_vv || params.row.ma_vv
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: params => params.row.ma_hd_data?.ma_hd || params.row.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: params => params.row.ma_dtt_data?.ma_dtt || params.row.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => params.row.ma_ku_data?.ma_ku || params.row.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: params => params.row.ma_phi_data?.ma_phi || params.row.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_sp_data?.ma_vt || params.row.ma_sp
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: params => params.row.ma_cp0_data?.code || params.row.ma_cp0
  },
  {
    field: 'gia1',
    headerName: 'Giá chuẩn',
    width: 120,
    columnType: 'number'
  },
  {
    field: 'gia2',
    headerName: 'Giá bán',
    width: 120,
    columnType: 'number'
  },
  {
    field: 'tien2',
    headerName: 'Thành tiền',
    width: 120,
    columnType: 'number'
  }
];

export const INPUT_TABLE_COLUMNS = buildInputTableCols(INPUT_TABLE_BASE_COLS);
